import { useEffect, useState, useCallback } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { useSignal } from '@backstage/plugin-signals-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import type { Entity } from '@backstage/catalog-model';
import type { JsonValue } from '@backstage/types';

export type PipelineStatus = 'running' | 'start' | 'stop' | 'failure' | 'unknown';

interface PipelineRunSignal {
  [key: string]: JsonValue | undefined;
  repoSlug: string;
  environmentName: string;
  conclusion: string;
  runUrl: string;
  finishedAt: string;
  actor: string;
}

export interface UsePipelineStatusResult {
  status: PipelineStatus;
  isTriggering: boolean;
  triggerError: string | null;
  runUrl: string | undefined;
  handleTrigger: (additionalParams?: string[]) => Promise<void>;
}

const VALID_STATUSES = new Set<string>(['running', 'start', 'stop', 'failure']);

export const statusToColor = (status: PipelineStatus): 'default' | 'primary' | 'secondary' => {
  switch (status) {
    case 'start':
      return 'primary';
    case 'stop':
    case 'failure':
      return 'secondary';
    default:
      return 'default';
  }
};

export const statusToLabel = (status: PipelineStatus): string => {
  switch (status) {
    case 'running':
      return 'Running\u2026';
    case 'start':
      return 'Started';
    case 'stop':
      return 'Stopped';
    case 'failure':
      return 'Failed';
    default:
      return 'Unknown';
  }
};

export function usePipelineStatus(entity: Entity): UsePipelineStatusResult {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [isTriggering, setIsTriggering] = useState(false);
  const [triggerError, setTriggerError] = useState<string | null>(null);
  const [signalStatus, setSignalStatus] = useState<PipelineStatus | undefined>(undefined);
  const [signalRunUrl, setSignalRunUrl] = useState<string | undefined>(undefined);
  const [initialStatus, setInitialStatus] = useState<PipelineStatus | undefined>(undefined);
  const [initialRunUrl, setInitialRunUrl] = useState<string | undefined>(undefined);

  const { lastSignal } = useSignal<PipelineRunSignal>('pipeline-run');

  const repoSlug = entity.metadata.annotations?.['github.com/project-slug'] ?? '';
  const envType = (entity.metadata.environment as { type?: string } | undefined)?.type;

  const entityRef = stringifyEntityRef(entity);

  // Fetch persisted status from the state file on the config-track branch on mount.
  useEffect(() => {
    setInitialStatus(undefined);
    setInitialRunUrl(undefined);
    setSignalStatus(undefined);
    setSignalRunUrl(undefined);
    setIsTriggering(false);
    setTriggerError(null);

    discoveryApi
      .getBaseUrl('dynamic-environment')
      .then(baseUrl => {
        const url = `${baseUrl}/environments/status?entityRef=${encodeURIComponent(entityRef)}`;
        return fetchApi.fetch(url);
      })
      .then(res => {
        if (!res.ok) throw new Error(`status ${res.status}`);
        return res.json();
      })
      .then((data: { lastAction?: string | null; workflowUrl?: string | null }) => {
        if (data.lastAction && VALID_STATUSES.has(data.lastAction)) {
          setInitialStatus(data.lastAction as PipelineStatus);
        }
        if (data.workflowUrl) {
          setInitialRunUrl(data.workflowUrl);
        }
      })
      .catch(() => {
        // Silently ignore — initial status stays undefined
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityRef]);

  // Match signals by env type (dev/int/qa) rather than entity name,
  // so updates work regardless of how the pipeline was triggered.
  useEffect(() => {
    if (lastSignal?.environmentName === envType && lastSignal?.repoSlug === repoSlug) {
      const conclusion = lastSignal.conclusion;
      if (conclusion === 'running') {
        setIsTriggering(true);
        setSignalStatus(undefined);
      } else {
        setIsTriggering(false);
        setSignalStatus(
          VALID_STATUSES.has(conclusion) ? (conclusion as PipelineStatus) : 'failure',
        );
        setSignalRunUrl(lastSignal.runUrl || undefined);
      }
    }
  }, [lastSignal, envType, repoSlug]);

  const status: PipelineStatus = isTriggering
    ? 'running'
    : signalStatus ?? initialStatus ?? 'unknown';

  const runUrl = signalRunUrl ?? initialRunUrl;

  const handleTrigger = useCallback(
    async (additionalParams?: string[]) => {
      try {
        setIsTriggering(true);
        setTriggerError(null);
        setSignalStatus(undefined);

        const baseUrl = await discoveryApi.getBaseUrl('dynamic-environment');
        const response = await fetchApi.fetch(`${baseUrl}/environments/trigger`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            entityRef: stringifyEntityRef(entity),
            ...(additionalParams !== undefined && { additionalParams }),
          }),
        });

        const data = (await response.json().catch(() => ({}))) as {
          error?: string;
          triggered?: boolean;
          webhookConfigured?: boolean;
        };

        if (!response.ok) {
          throw new Error(data.error ?? `Server error: ${response.status}`);
        }

        if (!data.webhookConfigured) {
          setIsTriggering(false);
        }
      } catch (err: any) {
        setTriggerError(err.message ?? 'Failed to trigger pipeline');
        setIsTriggering(false);
      }
    },
    [entity, discoveryApi, fetchApi],
  );

  return { status, isTriggering, triggerError, runUrl, handleTrigger };
}
